import { useEffect, useRef, useState } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useAuth } from "../utils/AuthContext"
import { toast } from "react-toastify"
import Spinner from "../components/Spinner/Spinner"
import LocationTermsModal from "../components/LocationTermsModal/LocationTermsModal"
import { gLogin, getUserData } from "../utils/auth"

const ProtectedRoute = ({ children, requiresAuth, redirectTo }) => {
    const { user, loading } = useAuth()
    const navigate = useNavigate()
    const location = useLocation() // Hook para acceder a la ubicación actual
    const hasRedirected = useRef(false)
    // Estado para controlar el modal de ubicación y términos
    const [showLocationModal, setShowLocationModal] = useState(false)
    // Calculamos el estado de Google login una sola vez al montar el componente
    const [isGoogleLogin, setIsGoogleLogin] = useState(false)

    // Efecto para determinar si es Google login una sola vez
    useEffect(() => {
        setIsGoogleLogin(gLogin())
    }, [])

    // Función para verificar si el usuario tiene información completa
    const hasCompleteUserInfo = (userData) => {

        const hasLocation = userData && userData.location;
        const hasTermsAccepted = userData && (
            userData.termsAccepted === true ||
            userData.termsAccepted === "true" ||
            userData.termsAccepted === 1
        );

        const isComplete = hasLocation && hasTermsAccepted;


        return isComplete;
    }

    useEffect(() => {
        // Solo ejecutamos la lógica de redirección si no estamos cargando y no hemos redirigido ya
        if (!loading && !hasRedirected.current) {
            const userData = getUserData();

            // Si requiere autenticación pero no hay usuario, redirigir
            if (requiresAuth && !user) {
                hasRedirected.current = true
                toast.info("Debes estar logueado para esta funcionalidad")
                // Detectar si viene de /registro/servicios o /registro/productos
                let providerType = null;
                if (location.pathname === "/registro/servicios") providerType = "service";
                if (location.pathname === "/registro/productos") providerType = "products";
                const stateToPass = {
                    ...(providerType ? { providerType } : (location.state?.providerType ? { providerType: location.state.providerType } : {})),
                    // Preservar la ubicación original para que el login pueda volver
                    from: { pathname: location.pathname, search: location.search }
                };
                navigate(redirectTo, { replace: true, state: stateToPass })
            }
            // Si hay usuario pero no tiene información completa, mostrar el modal
            else if (requiresAuth && user && !hasCompleteUserInfo(userData)) {
                setShowLocationModal(true)
            }
            // Si no requiere autenticación, hay usuario y no es login de Google, redirigir
            else if (!requiresAuth && user && !isGoogleLogin) {
                hasRedirected.current = true
                //toast.error("No debes estar logueado para esta funcionalidad")
                navigate("/", { replace: true })
            }
        }
    }, [loading, user, requiresAuth, redirectTo, navigate, location, isGoogleLogin]) // Añadir location a las dependencias

    // Resetear el flag de redirección cuando cambia el estado de autenticación
    useEffect(() => {
        return () => {
            hasRedirected.current = false
        }
    }, [user, loading])

    // Función que se ejecuta cuando el usuario completa exitosamente la información
    const handleLocationModalSuccess = () => {
        setShowLocationModal(false)
        toast.success("¡Información completada! Ya puedes acceder a todas las funcionalidades.")
        // Forzar una recarga del componente para reflejar los cambios
        window.location.reload()
    }

    // Función para intentar cerrar el modal (no permitido cuando es obligatorio)
    const handleLocationModalClose = () => {
        // Como es obligatorio, redirigir al usuario a la página principal
        toast.warning("Debes completar tu información para acceder a esta funcionalidad")
        navigate("/", { replace: true })
    }

    if (loading) {
        return <div><Spinner /></div>
    }

    const userData = getUserData();

    // Mostrar spinner mientras estamos por redirigir (solo para casos que no muestran modal)
    if ((requiresAuth && !user) ||
        (!requiresAuth && user && !isGoogleLogin)) {
        return <div><Spinner /></div>
    }

    // Si el usuario necesita completar información, mostrar el modal
    if (requiresAuth && user && !hasCompleteUserInfo(userData)) {
        return (
            <>
                {/* Mostrar un fondo o contenido base mientras se completa la información */}
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    zIndex: 999
                }}>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        flexDirection: 'column',
                        color: 'white',
                        textAlign: 'center',
                        padding: '20px'
                    }}>
                        <h2>¡Bienvenido!</h2>
                        <p>Para acceder a esta funcionalidad, necesitas completar tu información.</p>
                    </div>
                </div>

                <LocationTermsModal
                    isOpen={showLocationModal}
                    onClose={handleLocationModalClose}
                    onSuccess={handleLocationModalSuccess}
                    isMandatory={true}
                    title="Completa tu información para continuar"
                    description="Para acceder a esta funcionalidad, necesitas proporcionar tu ubicación y aceptar nuestros términos:"
                />
            </>
        )
    }

    return children
}

export default ProtectedRoute